import { ref, computed } from 'vue';
import {
  anchorFloorCoverage,
  anchorFamilies,
  spineCandidates,
  spineDefaults,
  spineRows,
  spineFits,
  assemblePicks,
  pickCountsBySource,
  anchorEvaluated,
  anchorHeadcount,
  levelCodeOf,
  titleRank,
  titleOf,
  idOf,
} from './anchorSetup.js';

/**
 * The guided anchor setup journey (PD-1258) — state and transitions, shared.
 *
 * Wraps the pure model in `anchorSetup.js` with the one fetch it needs and the
 * confirm step, so rm-portal and rm-portal-beta drive an identical wizard and only
 * supply markup. Nothing is written until `apply()`.
 *
 * @param {Object}   opts
 * @param {Object}   opts.http     axios-like instance
 * @param {Object}   opts.anchors  the levellingAnchors() self object (write path)
 * @param {Function} [opts.familyHtName] () => hierarchy type name carrying job family
 */
/**
 * The numbered rail, which depends on the SCENARIO.
 *
 * A register carrying legacy grade schemes is a transitioning ("mapping") customer;
 * one without is a greenfield build. That single distinction is what the coverage
 * step turns on:
 *
 *  - greenfield — nothing is levelled yet, so "27 of 30 levels short" is the STARTING
 *    STATE rather than a finding. The step reports failure by construction, which
 *    reads as "you have done this wrong" on a register where nothing could possibly
 *    be right yet. It is dropped, and the review step gains a row explaining that
 *    coverage accrues as evaluations land.
 *  - mapping — the same check has a real job (is there enough evidence to suggest a
 *    framework mapping from the existing grades?), so it stays, relabelled
 *    "Mapping coverage" to say what it is actually for.
 *
 * Exported as a function because of that. `ANCHOR_SETUP_STEPS` remains as the mapping
 * rail so existing callers and tests keep working.
 *
 * @param {'greenfield'|'mapping'} scenario
 */
export function anchorSetupSteps(scenario) {
  const steps = [
    { key: 'intro', label: 'How it works', head: 'Set your anchor roles' },
    { key: 'ceo', label: 'CEO & leadership', head: 'Start at the top' },
    { key: 'spine', label: 'Family spine', head: 'Anchor one family top-to-bottom' },
    { key: 'scatter', label: 'Cross-family', head: 'Spread across the remaining families' },
  ];
  if (scenario === 'mapping') {
    steps.push({ key: 'coverage', label: 'Mapping coverage', head: 'Enough roles to suggest a mapping' });
  }
  steps.push({ key: 'review', label: 'Review', head: 'Review before you set them' });
  return steps;
}

/** The full (mapping) rail. Prefer `wizard.steps` — it respects the scenario. */
export const ANCHOR_SETUP_STEPS = anchorSetupSteps('mapping');

/* Steps that sit OUTSIDE the numbered rail. `done` is the confirmation; `tasks` is
   the state the wizard reopens into once anchors exist — the outstanding
   evaluations, which is the work that actually remains. Neither is reachable by
   stepping forward, so neither belongs in ANCHOR_SETUP_STEPS. */
export const ANCHOR_SETUP_ASIDE_HEADS = {
  done: 'Anchors set',
  tasks: 'Anchor evaluations to run',
};

/**
 * Where the banner's open/closed preference is remembered.
 *
 * A VIEW preference, so localStorage rather than the server-backed column prefs: it is
 * per-browser, disposable, and nothing downstream depends on it.
 */
const BANNER_OPEN_KEY = 'lvl.anchorBannerOpen';

/** null = never set, so the default is derived. Storage can throw in private mode. */
function readBannerPref() {
  try {
    const v = localStorage.getItem(BANNER_OPEN_KEY);
    return v == null ? null : v === '1';
  } catch (err) {
    return null;
  }
}

/** How many families may form the spine. Two is plenty for vertical calibration. */
const MAX_SPINE_FAMILIES = 2;
/** Leadership candidates offered below the CEO. */
const LEADERSHIP_SHOWN = 6;
/** How many of them are pre-ticked — the executive band, not the whole list. */
const LEADERSHIP_PRESELECT = 3;

export function anchorSetupWizard({ http, anchors, familyHtName }) {
  const self = {
    step: ref('intro'),
    loading: ref(false),
    error: ref(null),
    saving: ref(false),
    /* Bulk-save progress. `apply()` does N sequential writes and THEN re-reads the
       register, so a single "saving" flag would spin through a phase that isn't
       saving. The phase says which, and the counts make the write phase
       determinate: 'writing' | 'refreshing' | null. */
    savingPhase: ref(null),
    savedCount: ref(0),
    saveTotal: ref(0),

    /* The whole levelling-eligible register as the server returned it. Read
       `self.roles` instead almost everywhere — it applies the doc-type filter. */
    allRoles: ref([]),
    codeMap: ref({}),
    orgSize: ref('standard'),
    /** Server-authoritative doc-type options: [{ k, n }], biggest first. */
    docTypes: ref([]),
    /** { [docType]: true } — SESSION state, deliberately never persisted. */
    docSel: ref({}),
    /* 'mapping' | 'greenfield' | null. Null until load() resolves it, so the rail
       never flickers from one shape to the other on first paint. */
    scenarioRaw: ref(null),

    // selections
    ceoId: ref(null),
    ceoQuery: ref(''),
    leadershipSel: ref({}),      // { [docId]: true }
    spineFamilies: ref([]),      // family names, max MAX_SPINE_FAMILIES
    spineSel: ref({}),           // { [family]: { [levelCode]: docId|null } }
    scatterSel: ref({}),         // { [family]: docId|null }
    familyQuery: ref(''),
    gapsOnly: ref(false),
    /* No setup-wide rationale (Sasha, Aug): it lives on the role and is optional.
       Deliberately absent rather than unused — see canConfirm. */

    /** Set once `apply()` succeeds — snapshotted, see the note there. */
    done: ref(null),
  };

  const isAnchorRole = (r) => !!(r && (r.isAnchor || (r.levelling && r.levelling.is_anchor)));
  const isPendingRole = (r) => !!(r && r.isPending);

  /* ---- what counts as a role (doc-type scope) ------------------------------- */

  /**
   * The doc-type filter on the first step — "What counts as a role?".
   *
   * A tenant's register holds several document types and only some of them are the
   * thing being levelled. The OPTIONS are server-authoritative: `getAnchorSetupData`
   * derives them from the same rows it returns, which are already constrained to
   * templates carrying the levelling questionnaire — so a type without that
   * questionnaire attached can never be offered here.
   *
   * The SELECTION is per-session by product decision, and deliberately NOT persisted:
   * reopening the wizard to add more anchors should be able to scope to a different
   * type without disturbing anyone else or leaving config behind. It therefore resets
   * to "everything" on every `load()`.
   */
  self.docTypesOn = computed(() =>
    self.docTypes.value.map((d) => d.k).filter((k) => self.docSel.value[k]));

  /**
   * Toggle a type in or out of scope, RE-SEEDING as it goes.
   *
   * The re-seed is not optional. Changing the scope changes which roles exist, so picks
   * made under the old scope can reference roles no longer in the journey — a CEO from
   * a type just switched off would otherwise still be written by `apply()`, setting a
   * ceiling on a role the user had just excluded. Re-seeding reads the filtered
   * register, so a stale pick cannot survive. It is done here rather than left to the
   * hosts precisely so neither portal can forget it.
   */
  self.toggleDocType = (key, on) => {
    self.docSel.value = { ...self.docSel.value, [key]: !!on };
    seedSelections();
  };

  /** True when the register has more than one type — the control is pointless otherwise. */
  self.showDocTypeFilter = computed(() => self.docTypes.value.length > 1);

  /**
   * The register the whole journey works from.
   *
   * Everything downstream — families, the CEO list, the spine, coverage, the picks —
   * derives from this rather than from `allRoles`, so deselecting a type removes those
   * roles from the journey ENTIRELY rather than merely hiding them from one list.
   *
   * With no types selected this is empty, which is the honest result: the review step
   * will have nothing to confirm and the step-one warning says why.
   */
  self.roles = computed(() => {
    if (!self.docTypes.value.length) return self.allRoles.value;
    const on = self.docSel.value;
    return self.allRoles.value.filter((r) => on[r.docType || 'Unspecified']);
  });

  self.roleById = computed(() => {
    const m = {};
    for (const r of self.roles.value) m[idOf(r)] = r;
    return m;
  });

  /* ---- load ---------------------------------------------------------------- */

  self.load = async () => {
    self.loading.value = true;
    self.error.value = null;
    try {
      const resp = await http.post('document/anchorSetupData', {
        familyHtName: familyHtName ? familyHtName() : undefined,
      });
      const data = (resp && resp.data && resp.data.Data) || {};
      self.allRoles.value = Array.isArray(data.roles) ? data.roles : [];
      self.codeMap.value = data.codeMap || {};
      self.orgSize.value = data.orgSize || 'standard';
      self.docTypes.value = Array.isArray(data.docTypes) ? data.docTypes : [];
      /* Everything on, every time: session-scoped by design. Set BEFORE seeding, since
         seedSelections reads the filtered register. */
      self.docSel.value = Object.fromEntries(self.docTypes.value.map((d) => [d.k, true]));
      self.scenarioRaw.value = data.scenario === 'mapping' || data.scenario === 'greenfield'
        ? data.scenario : null;
      seedSelections();
    } catch (err) {
      console.error('Failed to load anchor setup data:', err);
      self.error.value = 'Could not load the register for anchor setup.';
      self.allRoles.value = [];
      self.docTypes.value = [];
      self.docSel.value = {};
    } finally {
      self.loading.value = false;
    }
  };


  /* ---- scenario ------------------------------------------------------------- */

  /**
   * Transitioning customer or new build — inferred, never asked.
   *
   * The prototype reads `lvlGradeSchemes()`: a register carrying legacy grade schemes
   * is a transitioning customer. THAT CONCEPT DOES NOT EXIST IN OUR DATA MODEL. There
   * is no grade-scheme table, no legacy-grade column and no client setting naming one
   * (the only "Grade" in the schema is a per-template custom field, matched by
   * `part_type.external_api_field = 'GRADE'` — which only fires for part types
   * literally titled "Grade", so a client whose scheme is called "Band" or "Career
   * Level" would be silently misread). Guessing from it would mislabel customers, and
   * mislabelling costs a real step in the journey.
   *
   * So we infer from the one signal that is both visible and sound: a register with
   * NOTHING evaluated cannot be anything but greenfield, and that is exactly the case
   * where a coverage verdict is failure-by-construction. It is a sharp, stable rule —
   * it never flips back once the first evaluation lands, so the rail cannot change
   * shape under a user mid-journey.
   *
   * Deliberately conservative in the other direction too: unknown and mid-load both
   * resolve to 'mapping', so an unanswering server leaves a transitioning customer on
   * the FULL rail rather than silently dropping a step they need.
   *
   * `scenarioRaw` is the override hook. If a real signal is ever added — most likely a
   * client setting, since the underlying fact is not derivable — the server can put
   * `scenario` in the `document/anchorSetupData` payload and it wins outright, with no
   * change needed here.
   */
  self.scenario = computed(() => {
    if (self.scenarioRaw.value) return self.scenarioRaw.value;
    if (self.loading.value || !self.roles.value.length) return 'mapping';
    return self.roles.value.some((r) => anchorEvaluated(r)) ? 'mapping' : 'greenfield';
  });
  self.isGreenfield = computed(() => self.scenario.value === 'greenfield');
  /** The live rail. Hosts MUST render this, not the exported constant. */
  self.steps = computed(() => anchorSetupSteps(self.scenario.value));

  /* ---- derived model ------------------------------------------------------- */

  const modelOpts = () => ({
    codeMap: self.codeMap.value,
    isAnchor: isAnchorRole,
    isPending: isPendingRole,
    orgSize: self.orgSize.value,
  });

  self.families = computed(() => anchorFamilies(self.roles.value, modelOpts()));
  self.familyByName = computed(() => {
    const m = {};
    for (const f of self.families.value) m[f.family] = f;
    return m;
  });
  self.spineCandidates = computed(() => spineCandidates(self.families.value).slice(0, 8));

  /** Does ANY role in the register carry a level? Decides which signal ranks people. */
  self.anyLevelled = computed(() => self.roles.value.some((r) => levelCodeOf(r)));

  /**
   * Seniority order: band, then title, then headcount, then title text.
   *
   * Band is the real signal and stays first. Title rank is what makes the ordering
   * survive a register where no band exists — the case where filtering to levelled
   * roles previously left this step with an empty list and no way past it.
   */
  const seniorSort = (a, b) => {
    const cm = self.codeMap.value;
    const band = (r) => {
      const meta = cm[levelCodeOf(r)];
      return (meta && (meta.global ?? meta.band)) || 0;
    };
    return band(b) - band(a)
      || titleRank(b) - titleRank(a)
      || anchorHeadcount(b) - anchorHeadcount(a)
      || String(titleOf(a)).localeCompare(String(titleOf(b)));
  };

  /**
   * Ranked seniority list for the CEO step.
   *
   * A search spans every role, so an unevaluated CEO can still be found by name. With
   * no search the pool is levelled roles WHERE ANY EXIST — and the whole register
   * otherwise, ranked by title. That fallback is the point: filtering to `levelCodeOf`
   * on a greenfield register produced an empty list, so the one step that must be
   * completed (the ceiling) could not be.
   */
  self.ceoOptions = computed(() => {
    const q = self.ceoQuery.value.trim().toLowerCase();
    const pool = q
      ? self.roles.value.filter((r) =>
          `${titleOf(r)} ${r.family || ''}`.toLowerCase().includes(q))
      : (self.anyLevelled.value ? self.roles.value.filter((r) => levelCodeOf(r)) : self.roles.value);
    return [...pool].sort(seniorSort).slice(0, 8);
  });

  /**
   * The suggested CEO — badged, never pre-selected (Carl: "CEO - auto suggestion
   * would be a useful feature").
   *
   * Deliberately a suggestion and not a default: the ceiling is a consequential,
   * once-per-client decision that silently hides levels from every future
   * classification, so it stays a deliberate pick. Suppressed while searching, because
   * a search result is the user's own candidate rather than our recommendation.
   *
   * `fromTitle` tells the UI to say where the ranking came from — on an unlevelled
   * register this is a guess from job title and must not present itself as a finding.
   */
  self.ceoSuggested = computed(() => {
    if (self.ceoQuery.value.trim()) return null;
    const top = self.ceoOptions.value[0];
    if (!top) return null;
    return { role: top, id: idOf(top), fromTitle: !self.anyLevelled.value };
  });

  /**
   * The next most senior roles, excluding whoever is the CEO pick. Same levelled-else-
   * everything fallback as the CEO list, for the same reason.
   */
  self.leadershipOptions = computed(() => {
    const pool = self.anyLevelled.value
      ? self.roles.value.filter((r) => levelCodeOf(r))
      : self.roles.value;
    return [...pool]
      .filter((r) => idOf(r) !== self.ceoId.value)
      .sort(seniorSort)
      .slice(0, LEADERSHIP_SHOWN);
  });

  /** The rungs of one family's spine — level rows, or title tiers where no level exists. */
  self.spineRowsFor = (family) => spineRows(
    typeof family === 'string' ? self.familyByName.value[family] : family,
  );

  /**
   * Where each role is ALREADY claimed, keyed by role id.
   *
   * `assemblePicks` dedupes silently, so without this a role selected twice looks
   * accepted and then vanishes from the review count with no explanation (Sasha: "I
   * should not be given option to select roles I've already selected as anchors").
   * Every selector reads this to disable roles another step has taken.
   *
   * Precedence matches `assemblePicks` — first claim wins, so the label a role is
   * shown under is the same reason the review step will give for it.
   */
  self.claimMap = computed(() => {
    const m = {};
    const add = (id, label, tag) => { if (id && !m[id]) m[id] = { label, tag }; };

    add(self.ceoId.value, 'CEO ceiling', 'ceo');
    for (const r of self.leadershipOptions.value) {
      if (self.leadershipSel.value[idOf(r)]) add(idOf(r), 'leadership layer', 'lead');
    }
    for (const fam of self.spineFamilies.value) {
      const sel = self.spineSel.value[fam] || {};
      for (const key of Object.keys(sel)) add(sel[key], `${fam} spine`, `spine:${fam}:${key}`);
    }
    for (const fam of Object.keys(self.scatterSel.value)) {
      if (!self.spineFamilies.value.includes(fam)) {
        add(self.scatterSel.value[fam], fam, `fam:${fam}`);
      }
    }
    return m;
  });

  /**
   * The claim on a role, or null if it is free to pick.
   *
   * `ownTag` is what lets a selector ignore ITS OWN current selection — without it
   * every dropdown would report its own value as taken and disable it.
   */
  self.claimOf = (docId, ownTag) => {
    const c = self.claimMap.value[docId];
    return c && c.tag !== ownTag ? c.label : null;
  };

  self.scatterFamilies = computed(() =>
    self.families.value.filter((f) => !self.spineFamilies.value.includes(f.family)));

  /** Scatter list after the search box and the "gaps only" lens. */
  self.shownFamilies = computed(() => {
    const q = self.familyQuery.value.trim().toLowerCase();
    return self.scatterFamilies.value.filter(
      (f) =>
        (!q || f.family.toLowerCase().includes(q) || String(f.group).toLowerCase().includes(q)) &&
        (!self.gapsOnly.value || !f.existing),
    );
  });

  self.groupedFamilies = computed(() => {
    const m = {};
    for (const f of self.shownFamilies.value) (m[f.group] = m[f.group] || []).push(f);
    return Object.keys(m).sort().map((group) => ({ group, items: m[group] }));
  });

  self.picks = computed(() =>
    assemblePicks(
      {
        ceoId: self.ceoId.value,
        leadershipIds: Object.keys(self.leadershipSel.value)
          .filter((k) => self.leadershipSel.value[k])
          .map((k) => (isNaN(Number(k)) ? k : Number(k))),
        spineFamilies: self.spineFamilies.value,
        spineSel: self.spineSel.value,
        scatterSel: self.scatterSel.value,
      },
      self.roleById.value,
    ));

  /** Picks that aren't anchors yet — the only ones that will be written. */
  self.newPicks = computed(() => self.picks.value.filter((p) => !isAnchorRole(p.role)));
  /** New picks with no completed evaluation: these land as PENDING anchors. */
  self.pendingPicks = computed(() => self.newPicks.value.filter((p) => !anchorEvaluated(p.role)));
  self.pickCounts = computed(() => pickCountsBySource(self.newPicks.value));

  self.coverage = computed(() =>
    anchorFloorCoverage(self.roles.value, self.newPicks.value, modelOpts()));

  self.ceoRole = computed(() =>
    (self.ceoId.value != null ? self.roleById.value[self.ceoId.value] : null) || null);
  /** Would confirming move the ceiling? Drives the "moves from X" review line. */
  self.ceilingMoves = computed(() => {
    const cur = anchors.ceiling.value;
    const pick = self.ceoRole.value;
    if (!pick) return false;
    return !cur || Number(cur.doc_id) !== Number(idOf(pick));
  });
  /** Families deliberately left without a reference — surfaced, never silent. */
  self.skippedFamilies = computed(() =>
    self.scatterFamilies.value.filter((f) => self.scatterSel.value[f.family] == null && !f.existing));

  /* ---- seeding ------------------------------------------------------------- */

  function seedSelections() {
    /* Levelled where levels exist, the whole register otherwise. Seeding off levelled
       roles alone left EVERY selection empty on a greenfield register; falling back to
       the title-ranked order is what makes the journey completable there. */
    const ranked = [...(self.anyLevelled.value
      ? self.roles.value.filter((r) => levelCodeOf(r))
      : self.roles.value)].sort(seniorSort);

    /* An existing ceiling is a decision already taken, so it is restored. Otherwise
       the CEO is left UNSET even though we have a suggestion for it — see
       `self.ceoSuggested`: badged, not pre-selected. */
    const ceiling = anchors.ceiling.value;
    self.ceoId.value = ceiling ? Number(ceiling.doc_id) : null;

    // Pre-tick the executive band below the CEO, not the whole leadership list.
    const lead = {};
    for (const r of ranked.slice(1, 1 + LEADERSHIP_PRESELECT)) lead[idOf(r)] = true;
    self.leadershipSel.value = lead;

    /* Spine defaults to the best candidate, but only if it could BE a spine —
       `spineFits` accepts a real ladder (3+ levels) or, with no levels to read, a
       family with enough roles to form one. Two of anything is a pair, not a
       calibration. */
    const best = spineCandidates(self.families.value)[0];
    if (spineFits(best)) {
      self.spineFamilies.value = [best.family];
      self.spineSel.value = { [best.family]: spineDefaults(best, modelOpts()) };
    } else {
      self.spineFamilies.value = [];
      self.spineSel.value = {};
    }

    const scatter = {};
    for (const f of self.families.value) {
      scatter[f.family] = f.existing ? idOf(f.existing) : (f.suggested ? idOf(f.suggested) : null);
    }
    self.scatterSel.value = scatter;
  }

  /* ---- selection actions --------------------------------------------------- */

  self.toggleSpineFamily = (family) => {
    const on = self.spineFamilies.value.includes(family);
    if (on) {
      self.spineFamilies.value = self.spineFamilies.value.filter((f) => f !== family);
      const next = { ...self.spineSel.value };
      delete next[family];
      self.spineSel.value = next;
      return;
    }
    if (self.spineFamilies.value.length >= MAX_SPINE_FAMILIES) return;
    const fam = self.familyByName.value[family];
    if (!fam) return;
    self.spineFamilies.value = [...self.spineFamilies.value, family];
    self.spineSel.value = { ...self.spineSel.value, [family]: spineDefaults(fam, modelOpts()) };
  };

  self.setSpineLevel = (family, code, docId) => {
    const fam = self.spineSel.value[family] || {};
    self.spineSel.value = { ...self.spineSel.value, [family]: { ...fam, [code]: docId } };
  };

  /** Tick/untick a level in a spine; ticking restores that level's default pick. */
  self.toggleSpineLevel = (family, code, on) => {
    const fam = self.familyByName.value[family];
    const restore = on && fam ? spineDefaults(fam, modelOpts())[code] : null;
    self.setSpineLevel(family, code, on ? restore : null);
  };

  self.setScatter = (family, docId) => {
    self.scatterSel.value = { ...self.scatterSel.value, [family]: docId };
  };
  /** Fill every visible family that has no pick yet with its suggestion. */
  self.useAllSuggestions = () => {
    const next = { ...self.scatterSel.value };
    for (const f of self.shownFamilies.value) {
      if (next[f.family] == null && f.suggested) next[f.family] = idOf(f.suggested);
    }
    self.scatterSel.value = next;
  };
  /** Clear the visible families, but never an anchor that already exists. */
  self.clearShown = () => {
    const next = { ...self.scatterSel.value };
    for (const f of self.shownFamilies.value) if (!f.existing) next[f.family] = null;
    self.scatterSel.value = next;
  };

  self.toggleLeadership = (docId, on) => {
    self.leadershipSel.value = { ...self.leadershipSel.value, [docId]: !!on };
  };

  /* ---- navigation ---------------------------------------------------------- */

  /* Every navigation read goes through `self.steps`, never the exported constant —
     on greenfield the rail is one step shorter and `next()` from 'scatter' must land
     on 'review'. Reading the constant here would step into a 'coverage' screen the
     host does not render. */
  self.stepIndex = computed(() =>
    self.steps.value.findIndex((s) => s.key === self.step.value));
  self.stepMeta = computed(() => self.steps.value[self.stepIndex.value] || null);
  /* The dialog title, resolved here so both portals can't drift on it — the aside
     steps have no rail entry to read a head from. */
  self.headTitle = computed(() => ANCHOR_SETUP_ASIDE_HEADS[self.step.value]
    || (self.stepMeta.value && self.stepMeta.value.head) || '');
  /** True while on a step outside the numbered rail, so the host can hide it. */
  self.isAsideStep = computed(() => !!ANCHOR_SETUP_ASIDE_HEADS[self.step.value]);
  self.goto = (key) => { self.step.value = key; };
  self.next = () => {
    const rail = self.steps.value;
    const i = self.stepIndex.value;
    if (i >= 0 && i < rail.length - 1) self.step.value = rail[i + 1].key;
  };
  self.back = () => {
    const rail = self.steps.value;
    const i = self.stepIndex.value;
    if (i > 0) self.step.value = rail[i - 1].key;
  };

  /**
   * Confirm needs something to write — nothing more.
   *
   * It used to also require a setup-wide rationale. Removed on Sasha's feedback (Aug):
   * one sentence covering forty anchors says nothing defensible about any single one,
   * and having it here AND on the role was duplication. The rationale that survives
   * into audit is the per-role one, and it is RECOMMENDED, not required — so this must
   * never gate. On a greenfield register the old gate also blocked the journey
   * outright, since there is nothing sensible to write at setup time.
   */
  self.canConfirm = computed(
    () => self.newPicks.value.length > 0 || self.ceilingMoves.value);

  /* ---- confirm ------------------------------------------------------------- */

  /**
   * Write the picks. The CEO goes first and carries the ceiling; everything else is
   * an ordinary anchor. Unevaluated picks land as PENDING and confirm themselves
   * when their evaluation saves.
   */
  self.apply = async () => {
    if (!self.canConfirm.value) return false;

    /* Snapshot BEFORE writing. `newPicks` is derived from live anchor state, so each
       write empties it as the role becomes an anchor — reading it afterwards would
       report zero. The prototype hits the same trap and solves it the same way. */
    const snapshot = {
      total: self.picks.value.length,
      newCount: self.newPicks.value.length,
      pending: self.pendingPicks.value.map((p) => p.role),
      ceoRole: self.ceoRole.value,
    };

    const ceoId = self.ceoRole.value ? idOf(self.ceoRole.value) : null;
    const batch = [];
    if (ceoId != null && self.ceilingMoves.value) {
      batch.push({
        docId: ceoId,
        ceiling: true,
        pending: !anchorEvaluated(self.ceoRole.value),
      });
    }
    for (const p of self.newPicks.value) {
      if (ceoId != null && idOf(p.role) === ceoId) continue; // already in the batch
      batch.push({ docId: idOf(p.role), pending: !anchorEvaluated(p.role) });
    }

    self.saving.value = true;
    self.savingPhase.value = 'writing';
    self.savedCount.value = 0;
    self.saveTotal.value = batch.length;
    try {
      const result = await anchors.setAnchorBulk(batch, (done, total) => {
        self.savedCount.value = done;
        self.saveTotal.value = total;
      });
      self.done.value = { ...snapshot, ...result };
      self.step.value = 'done';
      // Re-read so the register the wizard was working from reflects what was
      // written. Still "busy", but no longer saving — say so.
      self.savingPhase.value = 'refreshing';
      await self.load();
      return result.failed.length === 0;
    } finally {
      self.saving.value = false;
      self.savingPhase.value = null;
    }
  };

  /* EVERY anchor still awaiting a full evaluation — whoever picked it, whenever.
     Not `pendingPicks`, which is only this session's selections: the point of the
     tasks step is the outstanding work across the register.
     Over `allRoles` for the same reason — this is a to-do list of work already
     committed to, so a session doc-type scope must not hide any of it. */
  self.pendingAll = computed(() => self.allRoles.value.filter((r) => isPendingRole(r)));
  self.hasPending = computed(() => self.pendingAll.value.length > 0);

  /**
   * Open the wizard in the right state.
   *
   * Once anchors exist, the useful thing is not the setup journey again — it is the
   * evaluations still to run, because a pending anchor is inert until evaluated. So
   * a register with outstanding work reopens straight to `tasks`, with the numbered
   * steps one click away ("Add more anchors").
   *
   * @param {'tasks'|'intro'} [mode] force a state; omitted = decide from the data
   */
  self.start = (mode) => {
    self.done.value = null;
    if (mode === 'tasks' || (!mode && self.hasPending.value)) { self.step.value = 'tasks'; return; }
    self.step.value = 'intro';
  };

  /**
   * The review step's anchor summary (PD-1258).
   *
   * The distinction that matters here is READY NOW versus AWAITING EVALUATION, and it
   * used to be split across two rows ("N new anchors" then "N pending evaluation"),
   * which reads as two facts rather than one shape. A pick with no full evaluation
   * behind it is a DRAFT anchor — inert until evaluated — so someone confirming needs
   * to know how much of what they are about to set is actually usable.
   */
  self.reviewSummary = computed(() => {
    const newN = self.newPicks.value.length;
    const pendN = self.pendingPicks.value.length;
    const counts = self.pickCounts.value;
    const fams = self.spineFamilies.value;

    const head = !newN ? 'No new anchors'
      : pendN === newN
        ? `${newN} role${newN === 1 ? '' : 's'} assigned to be anchored, awaiting evaluation`
        : pendN
          ? `${newN} new anchors — ${newN - pendN} ready now, ${pendN} awaiting evaluation`
          : `${newN} new anchor${newN === 1 ? '' : 's'}, ready now`;

    const sources = [
      counts.leadership ? `${counts.leadership} leadership` : null,
      counts.spine
        ? `${counts.spine} in the ${fams.join(' and ')} spine${fams.length > 1 ? 's' : ''}`
        : null,
      counts.scatter ? `${counts.scatter} across other families` : null,
      counts.ceo ? 'the CEO' : null,
    ].filter(Boolean).join(' · ');

    const body = !newN
      ? 'Everything you picked is already an anchor.'
      : `${sources}.`
        + (pendN
          ? ` ${pendN === newN ? 'None of these have' : `${pendN} of them do not have`}`
            + ' a full evaluation yet (a classification is not enough), so those are saved as draft'
            + ' anchors: each carries a dashed mark and an evaluation task, and becomes a working'
            + ' reference the moment its evaluation is saved.'
          : ' Each already carries a full evaluation, so it can be used as a reference straight away.');

    /* Everything pending means nothing you set today is usable yet — worth warning. */
    return { head, body, allPending: newN > 0 && pendN === newN };
  });

  /**
   * The greenfield closing row on the review step, in place of a coverage verdict.
   *
   * On a new register a coverage check can only ever report failure, so it is dropped
   * from the rail (see anchorSetupSteps) — but silently dropping it would leave the
   * user wondering whether coverage matters at all. It does; it just accrues later.
   * This row says so, and is the ONLY thing that replaces the step.
   *
   * Null on a mapping customer, who gets the real coverage step instead.
   */
  self.greenfieldCoverageNote = computed(() => {
    if (!self.isGreenfield.value) return null;
    const floor = self.coverage.value.floor;
    /* Segmented so the SIGNPOST can be emphasised the way the prototype emphasises it
       (<strong>Levelling framework</strong>). As one flat string it read as prose and the
       pointer — the whole purpose of the row — did not stand out. */
    return {
      head: 'Coverage builds as evaluations land',
      body: [
        { t: `A level becomes usable as a reference once ${floor} role${floor === 1 ? '' : 's'} at it`
          + ' are fully evaluated. Nothing here has to be complete before you start — the ' },
        { t: 'Levelling framework', strong: true },
        { t: ' grid’s Coverage view tracks it, and classification warns rather than blocks'
          + ' where evidence is thin.' },
      ],
    };
  });

  /** The scatter step's forward label — the next stop differs by scenario. */
  self.scatterNextLabel = computed(() =>
    (self.isGreenfield.value ? 'Review picks' : 'Check mapping coverage'));

  /**
   * Everything the coverage banner renders, in one place (PD-1258).
   *
   * The banner is deliberately a HIERARCHY, not a sentence: one short headline naming
   * the state, a bar, the supporting facts as small stats, then a single line of
   * guidance. It used to be one long clause-chain ("N of M levels meet the floor · CEO
   * ceiling set (E9) · N pending"), which reads as noise and buries whichever part
   * matters today.
   *
   * Four states, in precedence order: complete → work outstanding → nothing set yet →
   * partway. Note the CTA's *mode* keys off pending alone while its *label* keys off
   * `done` first: a register that is otherwise complete but has stragglers still says
   * "Review anchors", yet clicking it lands on those stragglers.
   */
  /**
   * Is the anchor set actually COMPLETE?
   *
   * Deliberately not coverage alone. The old rule was `pop > 0 && metNow >= pop &&
   * ceiling`, evaluated before the pending count was looked at — so a register with one
   * populated level and thirty-odd draft anchors reported "Anchors are in place":
   * `metNow >= pop` is trivially true when `pop` is 1, and the drafts were never
   * considered. Completeness now requires nothing outstanding AND a real ladder to be
   * complete against.
   */
  const anchorsThin = () => {
    const pop = self.coverage.value.pop || 0;
    /* Below three populated levels there is no ladder to measure coverage against —
       the same bar the wizard's spine step uses. "0 of 1 levels calibrated" reads as a
       target when it actually means "we have one levelled role to go on". */
    return pop > 0 && pop < 3;
  };

  const anchorsComplete = () => {
    const cov = self.coverage.value;
    const pop = cov.pop || 0;
    const ceil = (anchors && anchors.ceiling && anchors.ceiling.value) || null;
    return !self.pendingAll.value.length && !anchorsThin() && pop > 0 && cov.metNow >= pop && !!ceil;
  };

  self.banner = computed(() => {
    const cov = self.coverage.value;
    const ceil = (anchors && anchors.ceiling && anchors.ceiling.value) || null;
    const pendingN = self.pendingAll.value.length;
    const pop = cov.pop || 0;
    const done = anchorsComplete();
    const large = cov.orgSize === 'large';

    const head = done ? 'Anchors are in place'
      : pendingN ? `${pendingN} anchor${pendingN === 1 ? '' : 's'} awaiting evaluation`
        : !pop ? 'No anchors set yet'
          : `${cov.metNow} of ${pop} levels calibrated`;

    const hint = done
      ? 'Every level people sit at has enough evaluated anchors to calibrate against.'
      : pendingN
        ? 'They are marked as anchors but need a full evaluation before anything can be compared against them.'
        : !pop
          ? 'Anchor roles are the evaluated references the rest of your levelling is compared against.'
          : 'Levels below the floor have too little evidence to calibrate against. Work top-down: CEO, leadership, then one family in full.';

    /* Only facts that apply: the floor is meaningless with no populated levels, and a
       zero "awaiting evaluation" stat is noise. */
    const stats = [
      pop ? {
        key: 'Floor',
        value: `${cov.floor} per level${large ? ' · large org' : ''}`,
        tip: `A level is calibrated when ${cov.floor} roles at it are fully evaluated and marked as anchors${large ? ' — 3 rather than 2 because of this register’s size' : ''}.`,
      } : null,
      {
        key: 'CEO ceiling',
        value: ceil ? (ceil.levelCode || 'Set') : 'Not set',
        warn: !ceil,
        tip: ceil
          ? `The top of the framework is fixed at ${ceil.levelCode || 'this level'} by ${ceil.title}. Classification will not offer levels at or above it.`
          : 'Nothing fixes the top of the framework yet, so classification can place a role above your most senior role.',
      },
      pendingN ? {
        key: 'Awaiting evaluation',
        value: String(pendingN),
        warn: true,
        tip: 'Roles picked as anchors that still need a full point-scored evaluation. Each carries a dashed mark and an evaluation task.',
      } : null,
    ].filter(Boolean);

    const cta = {
      label: done ? 'Review anchors' : pendingN ? 'Finish evaluations' : 'Set up anchors',
      // Mode keys off pending ALONE — see the note above.
      mode: pendingN ? 'tasks' : undefined,
      ghost: done,
      title: done
        ? 'Reopen the anchor steps to review or add anchors.'
        : pendingN
          ? 'Open the list of anchor evaluations still to run — the same place you can add more anchors.'
          : 'Walk the guided setup: CEO, then the leadership layer, then one family in full, then across the rest.',
    };

    return {
      head, hint, stats, cta, done,
      showBar: pop > 0,
      pct: pop ? Math.round((cov.metNow / pop) * 100) : 0,
    };
  });

  /* ---- page-header state ----------------------------------------------------- */

  /**
   * Anchor state for the PAGE HEADER rather than a body banner (latest prototype).
   *
   * The banner cost ~120px of a viewport-bound data workspace, and its expanded detail
   * (floor, ceiling, pending count, guidance line) was reference material rather than
   * something needed at a glance. What matters — how many anchors still need evaluating
   * — reads as one line beside the page title, with the action beside the primary CTA.
   * The detail still exists; it is one click away in the wizard.
   *
   * Every string and every branch lives here, including the CTA's tooltip. In the
   * prototype that tooltip started life as an inline ternary on the button with its own
   * copy of these conditions, which is exactly how it came to promise "reopen the steps"
   * next to a "Set up anchors" label.
   */
  self.headState = computed(() => {
    const cov = self.coverage.value;
    const pop = cov.pop || 0;
    const pendingN = self.pendingAll.value.length;
    const thin = anchorsThin();
    const done = anchorsComplete();
    /* Any CONFIRMED anchor. Distinguishes "nothing set yet" from "set, but there are
       too few populated levels to measure coverage against" — which read identically
       before and sent the user back to a setup journey they had already completed. */
    const anchored = self.allRoles.value.some((r) => isAnchorRole(r) && !isPendingRole(r));

    const head = pendingN
      ? `${pendingN} draft anchor${pendingN === 1 ? '' : 's'} to evaluate`
      : done ? 'Well anchored'
        : (!pop || (thin && !anchored)) ? 'No anchors set yet'
          : thin ? 'Anchors set · too few levels to measure coverage'
            : `${cov.metNow} of ${pop} levels calibrated`;

    const tip = pendingN === 1
      ? '1 role is marked as an anchor but still needs a full evaluation — nothing can be'
        + ' compared against it until then. Open the anchor steps for the detail.'
      : pendingN
        ? `${pendingN} roles are marked as anchors but still need a full evaluation —`
          + ' nothing can be compared against them until then. Open the anchor steps for'
          + ' the detail.'
        : done
          ? 'Every level people sit at has an evaluated anchor to calibrate against, and'
            + ' nothing is waiting to be evaluated. Worth revisiting as the register'
            + ' changes — new roles, restructures and acquisitions can leave a level'
            + ' without a reference.'
          : !pop
            ? 'Anchor roles are the evaluated references the rest of your levelling is'
              + ' compared against.'
            : thin && !anchored
              ? `Only ${pop} level${pop === 1 ? ' is' : 's are'} populated so far, which is`
                + ' too few to measure coverage against. Set your anchors and the levels'
                + ' will follow from evaluating them.'
              : thin
                ? `Only ${pop} level${pop === 1 ? ' is' : 's are'} populated so far —`
                  + ' coverage becomes meaningful once more roles carry a level.'
                : `Levels below the floor of ${cov.floor} have too little evidence to`
                  + ' calibrate against.';

    const label = pendingN
      ? `Evaluate ${pendingN} draft anchor${pendingN === 1 ? '' : 's'}`
      : done ? 'View coverage'
        : (!pop || (thin && !anchored)) ? 'Set up anchors' : 'Add anchors';

    const ctaTip = pendingN
      ? 'Open the list of draft anchors still needing a full evaluation — the same place'
        + ' you can add more.'
      : done
        ? 'Anchors are set and every populated level has enough evidence. Opens the'
          + ' framework so you can see coverage per level and track.'
        : (!pop || (thin && !anchored))
          ? 'Walk the guided setup: CEO, then the leadership layer, then one family in'
            + ' full, then across the rest.'
          : thin
            ? 'Reopen the anchor steps to add more anchors — coverage per level becomes'
              + ' measurable once more roles carry a level.'
            : 'Reopen the anchor steps to cover the levels that are still thin.';

    return {
      done,
      pendingN,
      covPop: pop,
      head,
      tip,
      label,
      ctaTip,
      /* Neutral pill by default: a draft anchor is WORK, not a breach, and amber read as
         "something is wrong". Only the icon carries state. */
      tone: done ? 'done' : pendingN ? 'warn' : null,
      icon: done ? 'grid_view' : 'anchor',
      mdi: done ? 'mdi-view-grid-outline' : 'mdi-anchor',
      /* Complete: the useful destination is the framework's coverage view, not a setup
         journey there is nothing left to do in. Null = open the wizard. */
      to: done ? 'framework' : null,
      /* Which state the wizard should open in when `to` is null. */
      mode: pendingN ? 'tasks' : null,
    };
  });

  /* ---- banner collapse ------------------------------------------------------ */
  /* ORPHANED as of the page-header move: both portals now render `headState` in
     .lvl-pagehead instead of CcAnchorBanner, so `banner`, the collapse preference and
     CcAnchorBanner.vue itself have no callers. Left in place rather than deleted because
     removing a public surface is the host's call, not this module's — but nothing reads
     any of it today, and it should go if the banner is not coming back. */

  /**
   * The banner is collapsible, and defaults by STATE rather than to a constant.
   *
   * It plus its stat row leave roughly three visible rows of a thousand-row table
   * (Carl), so it has to be able to get out of the way. Open while there is still work
   * to do, closed once every populated level is calibrated — at which point it is a
   * status line, not a task, and should not cost the table three rows. An explicit user
   * choice always wins over that default, and persists.
   */
  self.bannerPref = ref(readBannerPref());

  self.bannerOpen = computed(() => (self.bannerPref.value == null
    ? !self.banner.value.done
    : self.bannerPref.value));

  self.setBannerOpen = (open) => {
    self.bannerPref.value = !!open;
    try {
      localStorage.setItem(BANNER_OPEN_KEY, open ? '1' : '0');
    } catch (err) {
      /* Private mode — the choice still applies for this session. */
    }
  };

  self.toggleBanner = () => self.setBannerOpen(!self.bannerOpen.value);

  /** Icon and tooltip for the toggle, so the two portals cannot drift on either. */
  self.bannerToggle = computed(() => (self.bannerOpen.value
    ? {
      open: true,
      icon: 'unfold_less',
      mdi: 'mdi-unfold-less-horizontal',
      title: 'Collapse this panel to give the roles table more room',
    }
    : {
      open: false,
      icon: 'unfold_more',
      mdi: 'mdi-unfold-more-horizontal',
      title: 'Show anchor coverage detail',
    }));

  /**
   * Active anchors already in the register — what "remove all" would clear.
   *
   * Counted over `allRoles`, NOT the doc-type-filtered set: `removeAll` clears every
   * anchor for the CLIENT, so counting the filtered set would have the button promise
   * to clear four and actually clear eight.
   */
  self.existingAnchorCount = computed(
    () => self.allRoles.value.filter((r) => isAnchorRole(r)).length,
  );

  /**
   * Clear every anchor for the client. The wizard is otherwise additive-only, so
   * without this a register installed by it could only be undone one role at a time.
   * Also the "start over" path while a framework is being configured.
   *
   * Reuses load() afterwards, which reseeds the selections — the previous picks
   * referenced roles that are no longer anchors, so keeping them would show stale
   * "Anchor" states against roles that no longer have the mark.
   *
   * @returns {Promise<number|null>} how many were removed, or null on failure
   */
  self.removeAll = async () => {
    self.saving.value = true;
    self.savingPhase.value = 'writing';
    try {
      const removed = await anchors.removeAnchors({ all: true });
      if (removed == null) return null;
      self.savingPhase.value = 'refreshing';
      await self.load();
      self.done.value = null;
      return removed;
    } finally {
      self.saving.value = false;
      self.savingPhase.value = null;
    }
  };

  // Re-exported so hosts don't each import the model directly.
  self.headcount = anchorHeadcount;
  self.evaluated = anchorEvaluated;
  self.levelCodeOf = levelCodeOf;
  self.titleOf = titleOf;
  self.idOf = idOf;

  return self;
}
